import { parseAndCleanJSON } from './gemini';

export type DocumentRole =
  | 'Syllabus'
  | 'Class Notes'
  | 'Teacher Material'
  | 'Textbook'
  | 'Question Bank'
  | 'Previous Paper'
  | 'Assignment'
  | 'Other';

export interface UploadedMaterial {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  role: DocumentRole;
  rawText?: string;
  base64Data?: string; // Pure Base64 string without data: prefix
  status: 'ready' | 'processing' | 'error';
  error?: string;
}

export interface SyllabusUnit {
  unitTitle: string;
  topics: string[];
  subtopics?: string[];
  examWeightage?: string;
}

export interface CrossDocumentInsights {
  frequentConcepts: string[];
  teacherEmphasizedTopics: string[];
  repeatedFormulas: string[];
  detectedQuestions: {
    question: string;
    sourceRole: DocumentRole;
    isPreviousPaper?: boolean;
    importanceLevel?: 'High' | 'Medium' | 'Essential';
  }[];
  conflictsDetected?: string[];
  missingSyllabusTopics?: string[];
}

export interface StudyMaterialAnalysis {
  subject: string;
  classLevel: string;
  board: string;
  chapterOrCourseTitle: string;
  syllabusMap: SyllabusUnit[];
  crossDocumentInsights: CrossDocumentInsights;
  sourcePrioritySummary: string[];
  highPriorityTopics: string[];
  totalDocumentsProcessed: number;
  estimatedLearningMinutes: number;
  recommendedPlanTitle: string;
}

export interface ExamPrepOptions {
  prepLevel: 'zero' | 'basics' | 'studied_revision' | 'exam_revision';
  availableTime: '30m' | '1h' | '2h' | 'masterclass';
  examTimeline: 'tomorrow' | '2days' | '1week' | 'flexible';
  language: string;
}

/**
 * Infer document role automatically from filename keywords
 */
export function inferDocumentRole(fileName: string): DocumentRole {
  const lower = fileName.toLowerCase();
  if (lower.includes('syllabus') || lower.includes('curriculum') || lower.includes('course_outline')) {
    return 'Syllabus';
  }
  if (lower.includes('teacher') || lower.includes('faculty') || lower.includes('prof') || lower.includes('slides')) {
    return 'Teacher Material';
  }
  if (lower.includes('question') || lower.includes('bank') || lower.includes('pyq') || lower.includes('paper') || lower.includes('exam')) {
    if (lower.includes('pyq') || lower.includes('previous') || lower.includes('past')) {
      return 'Previous Paper';
    }
    return 'Question Bank';
  }
  if (lower.includes('assignment') || lower.includes('worksheet') || lower.includes('classwork') || lower.includes('homework')) {
    return 'Assignment';
  }
  if (lower.includes('textbook') || lower.includes('chapter') || lower.includes('book')) {
    return 'Textbook';
  }
  if (lower.includes('note') || lower.includes('lecture') || lower.includes('class')) {
    return 'Class Notes';
  }
  return 'Other';
}

/**
 * Reads a File object from browser input and converts it to UploadedMaterial
 */
export async function readUploadedFile(file: File, customRole?: DocumentRole): Promise<UploadedMaterial> {
  const id = Math.random().toString(36).substring(2, 9) + '_' + Date.now();
  const role = customRole || inferDocumentRole(file.name);
  const mimeType = file.type || (file.name.endsWith('.pdf') ? 'application/pdf' : 'text/plain');

  try {
    const isTextLike = 
      mimeType.startsWith('text/') ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.md') ||
      file.name.endsWith('.json') ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.py') ||
      file.name.endsWith('.js') ||
      file.name.endsWith('.ts');

    if (isTextLike) {
      const text = await file.text();
      return {
        id,
        fileName: file.name,
        fileSize: file.size,
        mimeType: 'text/plain',
        role,
        rawText: text,
        status: 'ready'
      };
    } else {
      // Read as ArrayBuffer and convert to Base64
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Data = btoa(binary);

      return {
        id,
        fileName: file.name,
        fileSize: file.size,
        mimeType,
        role,
        base64Data,
        status: 'ready'
      };
    }
  } catch (err: any) {
    console.error(`Error reading file ${file.name}:`, err);
    return {
      id,
      fileName: file.name,
      fileSize: file.size,
      mimeType,
      role,
      status: 'error',
      error: err.message || 'Failed to read file content'
    };
  }
}

/**
 * Formats file size nicely
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Analyzes uploaded study materials using Gemini to generate a Syllabus Map and Exam Strategy.
 */
export async function analyzeStudyMaterials(
  materials: UploadedMaterial[],
  options: ExamPrepOptions,
  headers: Record<string, string>
): Promise<StudyMaterialAnalysis> {
  const readyMaterials = materials.filter(m => m.status === 'ready');
  if (readyMaterials.length === 0) {
    throw new Error("No readable files uploaded for analysis.");
  }

  // Build Gemini contents array with text and binary inlineData parts
  const promptText = `
  You are Professor X, an elite academic advisor and master professor preparing a student for their upcoming exam.
  The student has uploaded ${readyMaterials.length} study material documents for analysis:

  DOCUMENT INVENTORY & ROLES:
  ${readyMaterials.map((m, idx) => `${idx + 1}. ${m.fileName} (Role: ${m.role}, Size: ${formatFileSize(m.fileSize)})`).join('\n')}

  STUDENT CONTEXT:
  - Preparation Level: ${options.prepLevel} (zero = needs complete foundation; basics = knows fundamentals; studied_revision = needs focused recap; exam_revision = high-yield crash prep)
  - Target Study Time: ${options.availableTime}
  - Exam Timeline: ${options.examTimeline}
  - Language: ${options.language}

  YOUR TASK:
  Perform a multi-document cross-analysis of all attached documents.
  Extract and return strictly valid JSON matching this schema:
  {
    "subject": "Main Subject (e.g. Physics, Mathematics, Organic Chemistry, World History)",
    "classLevel": "Estimated Class / Grade (e.g. Class 10, Class 12, Undergraduate)",
    "board": "Estimated Board / University (e.g. CBSE, ICSE, University Standard)",
    "chapterOrCourseTitle": "Comprehensive title for this study session",
    "syllabusMap": [
      {
        "unitTitle": "Unit or Chapter Name",
        "topics": ["Topic A", "Topic B", "Topic C"],
        "subtopics": ["Subtopic A1", "Subtopic B1"],
        "examWeightage": "Estimated weightage or high priority tag"
      }
    ],
    "crossDocumentInsights": {
      "frequentConcepts": ["Concept repeated across notes & syllabus"],
      "teacherEmphasizedTopics": ["Concept heavily emphasized in teacher material or classwork"],
      "repeatedFormulas": ["Key formula 1", "Key formula 2"],
      "detectedQuestions": [
        {
          "question": "Exact or representative question found in files",
          "sourceRole": "Question Bank / Teacher Material / Previous Paper",
          "isPreviousPaper": true,
          "importanceLevel": "Essential"
        }
      ],
      "conflictsDetected": ["Any noted discrepancy between sources if present, otherwise empty"],
      "missingSyllabusTopics": ["Topics present in syllabus map but missing from notes if any"]
    },
    "sourcePrioritySummary": [
      "1. Teacher Material / Official Syllabus (Primary Truth)",
      "2. Class Notes & Assignments",
      "3. Previous Year Papers & Question Banks",
      "4. Supplementary Educational Concepts"
    ],
    "highPriorityTopics": ["Top 3-5 critical exam topics"],
    "totalDocumentsProcessed": ${readyMaterials.length},
    "estimatedLearningMinutes": 20,
    "recommendedPlanTitle": "Personalized Exam Prep Masterclass"
  }
  `;

  // Build Gemini parts
  const parts: any[] = [];
  parts.push({ text: promptText });

  readyMaterials.forEach((mat) => {
    parts.push({
      text: `\n\n=== ATTACHED STUDY MATERIAL: ${mat.fileName} [Role: ${mat.role}] ===\n`
    });

    if (mat.rawText) {
      parts.push({
        text: mat.rawText.slice(0, 30000) // Truncate extremely huge text if needed
      });
    } else if (mat.base64Data) {
      parts.push({
        inlineData: {
          mimeType: mat.mimeType,
          data: mat.base64Data
        }
      });
    }
  });

  const response = await fetch("/api/gemini/generate", {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      contents: [{ role: 'user', parts }],
      systemInstruction: "You are Professor X, master AI academic strategist. Analyze uploaded student study materials deeply and return valid JSON.",
      responseMimeType: "application/json"
    }),
  });

  if (!response.ok) {
    let errText = `Analysis Error: ${response.status}`;
    try {
      const errJson = await response.json();
      errText = errJson.error || errText;
    } catch (e) {}
    throw new Error(errText);
  }

  const resJson = await response.json();
  const parsed = parseAndCleanJSON(resJson.text || "") as unknown as StudyMaterialAnalysis;

  if (!parsed || !parsed.subject) {
    throw new Error("Failed to extract structured analysis from uploaded study materials.");
  }

  parsed.totalDocumentsProcessed = readyMaterials.length;
  return parsed;
}
